/**
 * @file Fieldset component for location input
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { OnInit, ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FormControl } from '@angular/forms';
import { FieldsetBase } from '../../base/fieldsets/fieldset-base/fieldset-base';
import { FieldsetProvider } from '../../base/fieldsets/fieldset-provider';
import { DomainService } from '../../domain.service';
import { ComponentRegistry } from '../../core/component-registry';
/**
 * Class for the location fieldset component
 */
var FieldsetLocationComponent = /** @class */ (function (_super) {
    tslib_1.__extends(FieldsetLocationComponent, _super);
    /**
     * Constructs a FieldsetLocationComponent
     * @param translateService Is needed to get a summary of the location.
     * @param provider Provides the configuration information for the fieldset.
     */
    function FieldsetLocationComponent(translateService, provider, domainService, changeDetectorRef) {
        var _this = _super.call(this, provider) || this;
        _this.domainService = domainService;
        _this.changeDetectorRef = changeDetectorRef;
        /**
         * Holds the colors used for displaying location
         * @private
         * @type {string[]}
         */
        _this.colors = [
            '#fb9fa7',
            '#0079a9',
            '#974463',
            '#57405d',
            '#54c1d9',
            '#999653',
            '#ffd46e',
            '#fa853b',
            '#f7674e',
            '#d31433',
            '#dbc996',
            '#674642',
            '#ef4e76',
            '#2e445b',
            '#306476',
            '#57815d',
            '#727158',
            '#e8a055',
            '#b44837',
            '#843e43'
        ];
        /**
         * Labels needed for address lines
         * @private
         * @type {{
         *         'APP.INPUTS.TEXT_PLACEHOLDER': string,
         *         'APP.INPUTS.NO_ENTITY': string
         *     }}
         */
        _this.labels = {
            'APP.INPUTS.TEXT_PLACEHOLDER': '?',
            'APP.INPUTS.NO_ENTITY': '?'
        };
        /**
         * Gets, sets the lines of the total address.
         * The property is equal to null when the address was not found.
         * @type {string[]}
         */
        _this.addressLines = null;
        /**
         * Is the fieldset in loading mode? E.g. trying to get an address based on
         * the user's input postal code and house number
         */
        _this.loading = false;
        /**
         * The reference of the entity that owns the address (location entity).
         * @private
         * @type {Entity}
         */
        _this._entity = null;
        /**
         * Gets, sets the read only mode of the component.
         */
        _this.readOnly = false;
        /**
         * Temporary field to hold house number value when user wants to edit this value and
         * the location entity is not created yet.
         * @private
         */
        _this._houseNumber = '';
        /**
         * Temporary field to hold postal code value when user wants to edit this value and
         * the location entity is not created yet.
         * @private
         */
        _this._postalCode = '';
        /**
         * Array containing errors after search.
         * @type {string[]}
         */
        _this.validationErrors = [];
        /** Angular controls are used in the template. */
        _this.houseNumberControl = new FormControl();
        _this.postalCodeControl = new FormControl();
        _this.streetControl = new FormControl();
        _this.cityControl = new FormControl();
        _this.subscriptionPool.add(translateService.get(['APP.INPUTS.TEXT_PLACEHOLDER', 'APP.INPUTS.NO_ENTITY']).subscribe(function (values) {
            _this.labels = values;
        }));
        return _this;
    }
    Object.defineProperty(FieldsetLocationComponent.prototype, "state", {
        /**
         * The state of the location fieldset is as follow:
         * empty  : no user input
         * auto   : address is found based on the input (postal code + house number)
         * manual : address is not found and the user can edit the adress himself.
         * @readonly
         * @type {string}
         */
        get: function () {
            if (this.loading) {
                return 'auto';
            }
            var test = this.location;
            if (!test) {
                return this.readOnly ? 'auto' : 'empty';
            }
            if (this.addressLines) {
                // Only filled when address was found or when readonly.
                return 'auto';
            }
            if (!test.entityAspect.entityState.isAdded()) {
                return 'manual';
            }
            if (this.validationErrors.length > 0) {
                // Search button is already used. (but not successful).
                return 'manual';
            }
            if ((test['Oid'] + '').lastIndexOf('_') === 1) {
                // Location is created for holding first time user input.
                return 'empty';
            }
            return 'manual';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FieldsetLocationComponent.prototype, "entity", {
        /**
         * Gets, the entity that owns the address (location entity).
         * Use this property for template bindings.
         * @readonly
         * @type {Entity}
         */
        get: function () {
            var test = this.provider.entity;
            if (test !== this._entity) {
                this.loading = false;
                this._entity = test;
                this._houseNumber = '';
                this._postalCode = '';
                // this.validationErrors.splice(0, this.validationErrors.length);
                this.validationErrors.length = 0;
                this.refreshAddressLines();
            }
            return test;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FieldsetLocationComponent.prototype, "location", {
        /**
         * Gets, the location entity representing the address of the entity shown.
         * @type {Entity}
         */
        get: function () {
            var _this = this;
            if (!this.entity) {
                return null;
            }
            var propertyName = this.provider.field.name;
            if (propertyName === '@this') {
                return this.entity;
            }
            var result = this.entity[propertyName];
            if (!result) {
                var property = this.entity.entityType.getNavigationProperty(propertyName);
                if (!property) {
                    return;
                }
                var foreignKeyProp = property.foreignKeyNames[0];
                var foreignKey = this.entity[foreignKeyProp];
                if (!foreignKey) {
                    return;
                }
                if (this.loading) {
                    return result;
                }
                this.loading = true;
                // window.setTimeout werkte niet. Daarom zwaar geschut met changeDetectorRef
                this.changeDetectorRef.detach();
                this.entity.entityAspect.loadNavigationProperty(propertyName, function (data) {
                    if (data.results.length === 1) {
                        result = data.results[0];
                        _this.refreshAddressLines();
                    }
                    _this.loading = false;
                    _this.changeDetectorRef.detectChanges();
                    _this.changeDetectorRef.reattach();
                });
                return result;
            }
            return result;
        },
        /**
         * Sets, the location entity representing the address of the entity shown.
         */
        set: function (value) {
            if (!this.entity) {
                return;
            }
            var propertyName = this.provider.field.name;
            var result = this.entity[propertyName];
            if (result && ((value && result['Oid'] !== value['Oid']) || value == null)) {
                // Reset previous location/address maybe it is used by others.
                if (result['$CHANGED_BY_ENTITY']) {
                    delete result['$CHANGED_BY_ENTITY'];
                    delete result['$CHANGED_BY_PROPERTY'];
                }
                result.entityAspect.rejectChanges();
            }
            this.entity[propertyName] = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FieldsetLocationComponent.prototype, "houseNumber", {
        /**
         * Gets, the house number of the address.
         * @type {string}
         */
        get: function () {
            var address = this.location;
            if (address) {
                return address['HouseNumber'];
            }
            return this._houseNumber;
        },
        /**
         * Sets, the house number of the address.
         */
        set: function (value) {
            var address = this.location;
            if (address) {
                if (this.updateValue(address, 'HouseNumber', value)) {
                    address['$CHANGED_BY_ENTITY'] = this.entity;
                    address['$CHANGED_BY_PROPERTY'] = this.provider.field.name;
                }
                return;
            }
            if (this._houseNumber === value) {
                return; // Deel van fix: Merkwaardige validatiemeldingen bij adres #2127
            }
            this._houseNumber = value;
            if (this._houseNumber !== '') {
                this.createNewLocation();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FieldsetLocationComponent.prototype, "postalCode", {
        /**
         * Gets, the postal code of the address
         * @type {string}
         */
        get: function () {
            var address = this.location;
            if (address) {
                return address['ZipCode'];
            }
            return this._postalCode;
        },
        /**
         * Sets, the postal code of the address
         */
        set: function (value) {
            var address = this.location;
            if (address) {
                if (this.updateValue(address, 'ZipCode', value)) {
                    address['$CHANGED_BY_ENTITY'] = this.entity;
                    address['$CHANGED_BY_PROPERTY'] = this.provider.field.name;
                    address['Color'] = this.pickColor(value);
                }
                return;
            }
            if (this._postalCode === value) {
                return; // Deel van fix: Merkwaardige validatiemeldingen bij adres #2127
            }
            this._postalCode = value;
            if (this._postalCode !== '') {
                this.createNewLocation();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FieldsetLocationComponent.prototype, "street", {
        /**
         * Gets, the street name of the address.
         * @type {string}
         */
        get: function () {
            var address = this.location;
            if (address) {
                return address['Address'];
            }
            return '';
        },
        /**
         * Sets, the street name of the address.
         */
        set: function (value) {
            var address = this.location;
            if (address) {
                this.updateValue(address, 'Address', value);
                return;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FieldsetLocationComponent.prototype, "city", {
        /**
         * Gets, the name of the city of the address.
         * @type {string}
         */
        get: function () {
            var address = this.location;
            if (address) {
                return address['Name'];
            }
            return '';
        },
        /**
         * Sets, the name of the city of the address.
         */
        set: function (value) {
            var address = this.location;
            if (address) {
                this.updateValue(address, 'Name', value);
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Register component by the fieldset provider
     * @static
     */
    FieldsetLocationComponent.register = function () {
        ComponentRegistry.register('fieldset', 'location', FieldsetLocationComponent);
    };
    /**
     * Search location by matching postal code and house number.
     * Creates a location without a waypoint when no match is found.
     * @returns {Observable<QueryResult>} Result set of query.
     */
    FieldsetLocationComponent.prototype.search = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var result, reason_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // this.validationErrors.splice(0, this.validationErrors.length);
                        this.validationErrors.length = 0;
                        this.loading = true;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.domainService.locationByZipCode(this.postalCode, this.houseNumber)];
                    case 2:
                        result = _a.sent();
                        if (result.results.length > 0) {
                            this.location = result.results[0];
                            this.refreshAddressLines();
                        }
                        else {
                            this.validationErrors.push('APP.DOMAIN.VALIDATORS.ZIPCODE_NUMBER_NOT_FOUND');
                            if (!this.entity.entityAspect.entityState.isUnchanged()) {
                                this.createNewLocation();
                            }
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        reason_1 = _a.sent();
                        this.validationErrors.push(reason_1);
                        this.loading = false;
                        return [3 /*break*/, 4];
                    case 4:
                        this.loading = false;
                        return [2 /*return*/, result];
                }
            });
        });
    };
    /**
     * Creates a new location entity without a waypoint to support storage of unknown addresses.
     * @private
     */
    FieldsetLocationComponent.prototype.createNewLocation = function () {
        var entity = this.domainService.getEntityType('Location').createEntity({
            Region: 'A',
            HouseNumber: this.houseNumber,
            ZipCode: this.postalCode,
            Name: this.city,
            Address: this.street,
            Color: this.pickColor(this.postalCode) // A location must have a valid color.
        });
        this.location = this.domainService.addEntity(entity);
        this._houseNumber = '';
        this._postalCode = '';
        this.refreshAddressLines();
    };
    /**
     * Resets the addressLines array and fills it with the
     * lines for showing the total address when the waypoint is known
     * or when the fieldset is in readonly mode.
     * @private
     */
    FieldsetLocationComponent.prototype.refreshAddressLines = function () {
        this.addressLines = null;
        if (!this.location) {
            if (this.readOnly) {
                this.addressLines = [this.labels['APP.INPUTS.NO_ENTITY']];
            }
            return;
        }
        if (!this.readOnly && this.location['Longitude'] === '') {
            return;
        }
        this.addressLines = [];
        this.addressLines.push(this.street + ' ' + this.houseNumber);
        this.addressLines.push(this.postalCode + ' ' + this.city);
    };
    /**
     * Is called right after the directive's data-bound properties have been checked for the
     * first time, and before any of its children have been checked.It is invoked only once when the
     * directive is instantiated.
     */
    FieldsetLocationComponent.prototype.ngOnInit = function () {
        this.readOnly = this.provider.field.readonly ? true : false;
    };
    /**
     * Returns whether the location exists and therefore can be deleted
     * @returns {boolean} A value indicating whether the location can be deleted by the user
     */
    FieldsetLocationComponent.prototype.canDelete = function () {
        return this.provider.entity && this.provider.entity[this.provider.field.name] != null && !this.readOnly;
    };
    /**
     * Removes the location from its parent and set it for deletion
     */
    FieldsetLocationComponent.prototype.delete = function () {
        if (!this.location) {
            return;
        }
        this.addressLines = null;
        this._houseNumber = '';
        this._postalCode = '';
        this.location['Address'] = '';
        this.location['Name'] = '';
        if (this.location.entityAspect.entityState.isAdded()) {
            this.location.entityAspect.rejectChanges();
        }
        else if (this.location.entityAspect.entityState.isUnchangedOrModified()) {
            this.location.entityAspect.setDeleted();
        }
        this.validationErrors.length = 0;
        if (this.entity) {
            this.entity[this.provider.field.name] = null;
            var property = this.entity.entityType.getNavigationProperty(this.provider.field.name);
            if (!property) {
                return;
            }
            this.entity[property.foreignKeyNames[0]] = '';
        }
    };
    /**
     * Sets an address property of the location entity when
     * the value is different from the current value
     * and removes the waypoint information.
     * @private
     * @param {Entity} address The location entity
     * @param {string} propertyName The name of the address property
     * @param {string} value The new value
     * @returns {boolean} True when the value was different from the current value otherwise false
     */
    FieldsetLocationComponent.prototype.updateValue = function (address, propertyName, value) {
        if (address[propertyName] !== value) {
            address[propertyName] = value;
            address['Longitude'] = '';
            address['Latitude'] = '';
            this.addressLines = null;
            return true;
        }
        return false;
    };
    /**
     * Method to pick a color based on the zipcode.
     * @private
     * @param {string} zipCode  The zipcode picking the color
     * @returns {string} The color code.
     */
    FieldsetLocationComponent.prototype.pickColor = function (zipCode) {
        // Count the char codes
        var charCodeCount = 0;
        for (var i = 0; i < zipCode.length; i++) {
            charCodeCount += zipCode.charCodeAt(i);
        }
        // And use this together with the remainder function to
        // map the numbers to the available colors.
        var remainder = (charCodeCount % this.colors.length);
        // Pick the color and return it
        return this.colors[remainder];
    };
    return FieldsetLocationComponent;
}(FieldsetBase));
export { FieldsetLocationComponent };
